import React from 'react';
import termsContent from '../../content/legal/terms.json';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=DM+Serif+Display&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #eef1f7;
    --surface: #ffffff;
    --text-dark: #1a2340;
    --text-body: #4a5568;
    --text-muted: #718096;
    --accent: #4a6fa5;
    --accent-light: #6b8fc4;
    --border: #dde3ee;
  }

  body {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--text-body);
    min-height: 100vh;
  }

  .tou-header {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    padding: 0 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 60px;
    position: sticky;
    top: 0;
    z-index: 100;
  }
  .tou-logo {
    font-family: 'DM Serif Display', serif;
    font-size: 1.25rem;
    color: var(--text-dark);
    letter-spacing: -0.02em;
  }
  .tou-logo span { color: var(--accent); }
  .tou-nav a {
    font-size: 0.85rem;
    color: var(--text-muted);
    text-decoration: none;
    margin-left: 24px;
    transition: color 0.2s;
  }
  .tou-nav a:hover { color: var(--accent); }

  .tou-page-wrapper {
    max-width: 860px;
    margin: 0 auto;
    padding: 0px 24px 80px;
  }

  .tou-breadcrumb {
    font-size: 0.8rem;
    color: var(--text-muted);
    margin-bottom: 28px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .tou-breadcrumb a { color: var(--accent); text-decoration: none; }
  .tou-breadcrumb a:hover { text-decoration: underline; }
  .tou-breadcrumb svg { width: 12px; height: 12px; opacity: 0.5; }

  .tou-hero {
    background: var(--surface);
    border-radius: 16px;
    padding: 40px 48px;
    margin-bottom: 32px;
    border: 1px solid var(--border);
    display: flex;
    align-items: flex-start;
    gap: 32px;
  }
  .tou-hero-icon {
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, #2d4a8a 0%, #4a6fa5 100%);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .tou-hero-icon svg { width: 26px; height: 26px; fill: white; }
  .tou-hero-text h1 {
    font-family: 'DM Serif Display', serif;
    font-size: 2rem;
    color: var(--text-dark);
    line-height: 1.2;
    margin-bottom: 10px;
    letter-spacing: -0.02em;
  }
  .tou-hero-meta { display: flex; gap: 20px; flex-wrap: wrap; }
  .tou-meta-pill {
    font-size: 0.78rem;
    font-weight: 500;
    color: var(--text-muted);
    background: var(--bg);
    border: 1px solid var(--border);
    padding: 4px 12px;
    border-radius: 20px;
  }

  .tou-content-card {
    background: var(--surface);
    border-radius: 16px;
    border: 1px solid var(--border);
    overflow: hidden;
  }

  .tou-toc-bar {
    background: #f7f9fd;
    border-bottom: 1px solid var(--border);
    padding: 16px 40px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
  }
  .tou-toc-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-right: 4px;
  }
  .tou-toc-link {
    font-size: 0.78rem;
    color: var(--accent);
    text-decoration: none;
    background: white;
    border: 1px solid var(--border);
    padding: 4px 10px;
    border-radius: 6px;
    transition: background 0.15s, color 0.15s;
  }
  .tou-toc-link:hover {
    background: var(--accent);
    color: white;
    border-color: var(--accent);
  }

  .tou-policy-body { padding: 40px 48px 48px; }

  .tou-section {
    margin-bottom: 44px;
    scroll-margin-top: 80px;
  }

  /* Intro (no card UI) */
  .tou-intro {
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 4px 0 8px;
  }
  .tou-intro p {
    font-size: 0.95rem;
    line-height: 1.85;
    color: var(--text-body);
  }
  .tou-intro a {
    color: var(--accent);
    font-weight: 600;
    text-decoration: none;
  }
  .tou-intro a:hover { text-decoration: underline; }
  .tou-intro h3 {
    font-size: 0.74rem;
    font-weight: 800;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin: 12px 0 6px;
  }
  .tou-inline-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 10px;
    margin-top: 6px;
  }
  .tou-inline-list-item {
    border: 1px solid var(--border);
    border-radius: 999px;
    padding: 6px 10px;
    background: #ffffff;
    font-size: 0.82rem;
    color: var(--text-body);
    line-height: 1.2;
    text-align: center;
    white-space: nowrap;
  }
  @media (max-width: 520px) {
    .tou-inline-list {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  .tou-section-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 20px;
  }
  .tou-section-number {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, #2d4a8a 0%, #4a6fa5 100%);
    color: white;
    font-size: 0.8rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .tou-section-title {
    font-family: 'DM Serif Display', serif;
    font-size: 1.2rem;
    color: var(--text-dark);
    letter-spacing: -0.01em;
  }

  .tou-clause {
    margin-bottom: 16px;
    padding: 16px 20px;
    background: #f9fafd;
    border-radius: 10px;
    border-left: 3px solid var(--border);
    transition: border-color 0.2s;
  }
  .tou-clause:hover { border-left-color: var(--accent-light); }

  .tou-clause-label {
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--accent);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 6px;
  }
  .tou-clause p {
    font-size: 0.92rem;
    line-height: 1.75;
    color: var(--text-body);
  }
  .tou-clause p + p { margin-top: 10px; }

  .tou-clause ul {
    list-style: none;
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 7px;
  }
  .tou-clause ul li {
    font-size: 0.9rem;
    color: var(--text-body);
    display: flex;
    align-items: flex-start;
    gap: 10px;
    line-height: 1.6;
  }
  .tou-clause ul li::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent);
    flex-shrink: 0;
    margin-top: 7px;
  }

  .tou-disclaimer-block {
    background: #fff8f0;
    border: 1px solid #f0d9b5;
    border-left: 4px solid #c97b2e;
    border-radius: 10px;
    padding: 18px 22px;
    margin-bottom: 16px;
  }
  .tou-disclaimer-block .tou-clause-label { color: #c97b2e; }
  .tou-disclaimer-block p {
    font-size: 0.88rem;
    line-height: 1.7;
    color: #5a4020;
  }

  .tou-info-items {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 14px;
  }
  .tou-info-tag {
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--accent);
    background: #eef3fb;
    border: 1px solid #c8d8f0;
    padding: 6px 14px;
    border-radius: 20px;
  }

  hr.tou-section-divider {
    border: none;
    border-top: 1px solid var(--border);
    margin: 32px 0;
  }

  .tou-footer-strip {
    background: var(--surface);
    border: 1px solid var(--border);
    padding: 20px 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 32px;
    border-radius: 16px;
  }
  .tou-footer-strip p { font-size: 0.78rem; color: var(--text-muted); }
  .tou-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--accent);
    text-decoration: none;
    background: var(--bg);
    border: 1px solid var(--border);
    padding: 8px 16px;
    border-radius: 8px;
    transition: background 0.15s;
  }
  .tou-back-btn:hover { background: #dde3f0; }

  @media (max-width: 600px) {
    .tou-hero { flex-direction: column; padding: 28px 24px; }
    .tou-policy-body { padding: 28px 24px; }
    .tou-toc-bar { padding: 14px 24px; }
    .tou-header { padding: 0 20px; }
  }
`;

export default function Terms({ embedded = false }) {
  const legalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    :root { --bg:#EEF2FA; --surface:#FFFFFF; --surface-muted:#F7F9FC; --border:#D4DEEF; --border-light:#E8EDF6; --text:#1B2540; --text-muted:#5A6785; --text-dimmed:#8C96AB; --primary:#4472C4; --primary-light:#E2EAFB; --radius-md:12px; --radius-lg:16px; --shadow-sm:0 1px 3px rgba(30,50,100,0.06),0 1px 2px rgba(30,50,100,0.04); --font-display:'Plus Jakarta Sans',-apple-system,sans-serif; --font-body:'DM Sans',-apple-system,sans-serif; }
    *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:var(--font-body);background:var(--bg);color:var(--text);min-height:100vh;}
    .legal-page{max-width:760px;margin:0 auto;padding:26px 18px 42px;}
    .legal-shell{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-lg);box-shadow:var(--shadow-sm);overflow:hidden;}
    .legal-head{display:flex;align-items:center;justify-content:space-between;padding:18px 22px 14px;border-bottom:1px solid var(--border-light);background:linear-gradient(180deg,var(--surface) 0%,var(--surface-muted) 100%);}
    .legal-head h1{font-family:var(--font-display);font-size:1.1rem;font-weight:800;color:var(--text);letter-spacing:-0.02em;}
    .legal-body{padding:18px 22px 26px;text-align:left;}
    .legal-meta{display:flex;align-items:center;gap:8px;margin-bottom:16px;padding-bottom:14px;border-bottom:1px solid var(--border-light);}
    .legal-meta-badge{display:inline-flex;align-items:center;gap:5px;padding:5px 12px;border-radius:100px;font-size:0.7rem;font-weight:600;background:var(--primary-light);color:var(--primary);}
    .legal-meta-badge svg{width:12px;height:12px;}
    .legal-meta-date{font-size:0.72rem;color:var(--text-dimmed);font-weight:500;}
    .legal-body h3{font-family:var(--font-display);font-size:0.95rem;font-weight:700;color:var(--text);margin:22px 0 8px;letter-spacing:-0.01em;text-align:left;}
    .legal-body h3:first-of-type{margin-top:0;}
    .legal-body p{font-size:0.84rem;color:var(--text-muted);line-height:1.7;margin-bottom:12px;text-align:left;}
    .legal-body ul{padding-left:20px;margin-bottom:12px;}
    .legal-body li{font-size:0.84rem;color:var(--text-muted);line-height:1.7;margin-bottom:6px;text-align:left;}
    .legal-body li::marker{color:var(--primary);}
    .legal-body strong{color:var(--text);font-weight:600;}
    .legal-body a{color:var(--primary);font-weight:600;text-decoration:none;}
    .legal-body a:hover{text-decoration:underline;}
    .legal-callout{display:flex;gap:12px;padding:14px 16px;border-radius:var(--radius-md);background:var(--primary-light);border:1px solid rgba(68,114,196,0.1);margin:16px 0;}
    .legal-callout svg{width:16px;height:16px;flex-shrink:0;color:var(--primary);margin-top:2px;}
    .legal-callout p{margin:0;font-size:0.82rem;color:#2C4A80;line-height:1.55;font-weight:500;}
    .legal-embed{background:transparent;}
    .legal-embed .legal-page{max-width:none;margin:0;padding:0;}
    .legal-embed .legal-shell{border:none;box-shadow:none;border-radius:0;}
    .legal-embed .legal-head{display:none;}
    .legal-embed .legal-body{padding:0;}
  `;

  const renderInline = (text) => {
    if (!text) return null;

    const withBreaks = String(text).split('\n');
    const lines = withBreaks.map((line, idx) => (
      // eslint-disable-next-line react/no-array-index-key
      <React.Fragment key={idx}>
        {renderInlineNoBreaks(line)}
        {idx < withBreaks.length - 1 ? <br /> : null}
      </React.Fragment>
    ));
    return lines;
  };

  const renderInlineNoBreaks = (text) => {
    const nodes = [];
    const s = String(text);
    let i = 0;

    const pushText = (t) => {
      if (t) nodes.push(t);
    };

    while (i < s.length) {
      // link: [label](url)
      if (s[i] === '[') {
        const closeLabel = s.indexOf(']', i + 1);
        const openUrl = closeLabel >= 0 ? s.indexOf('(', closeLabel + 1) : -1;
        const closeUrl = openUrl >= 0 ? s.indexOf(')', openUrl + 1) : -1;
        if (closeLabel >= 0 && openUrl === closeLabel + 1 && closeUrl >= 0) {
          const label = s.slice(i + 1, closeLabel);
          const url = s.slice(openUrl + 1, closeUrl);
          nodes.push(
            <a key={`a-${i}`} href={url}>
              {label}
            </a>
          );
          i = closeUrl + 1;
          continue;
        }
      }

      // bold: **text**
      if (s[i] === '*' && s[i + 1] === '*') {
        const end = s.indexOf('**', i + 2);
        if (end >= 0) {
          const boldText = s.slice(i + 2, end);
          nodes.push(<strong key={`b-${i}`}>{boldText}</strong>);
          i = end + 2;
          continue;
        }
      }

      // plain text chunk until next special token
      const nextLink = s.indexOf('[', i);
      const nextBold = s.indexOf('**', i);
      const next = [nextLink, nextBold]
        .filter((n) => n >= 0)
        .sort((a, b) => a - b)[0];
      if (next === undefined) {
        pushText(s.slice(i));
        break;
      }
      pushText(s.slice(i, next));
      i = next;
    }

    return nodes;
  };

  const doc = termsContent;

  return (
    <>
      <style>{legalStyles}</style>
      <div className={embedded ? 'legal-embed' : undefined}>
        <div className="legal-page">
          <div className="legal-shell">
            <div className="legal-head">
              <h1>{doc.title}</h1>
            </div>
            <div className="legal-body">
              <div className="legal-meta">
                <span className="legal-meta-badge">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  Version {doc.version}
                </span>
                <span className="legal-meta-date">
                  Effective: {doc.effectiveDate}
                </span>
              </div>

              {doc.sections.map((section) => (
                <React.Fragment key={section.id}>
                  <h3>{section.title}</h3>
                  {section.blocks.map((block, idx) => {
                    if (block.type === 'paragraph') {
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <p key={`${section.id}-p-${idx}`}>
                          {renderInline(block.text)}
                        </p>
                      );
                    }
                    if (block.type === 'list') {
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <ul key={`${section.id}-ul-${idx}`}>
                          {block.items.map((item, itemIdx) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={`${section.id}-li-${idx}-${itemIdx}`}>
                              {renderInline(item)}
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    if (block.type === 'callout') {
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <div
                          key={`${section.id}-c-${idx}`}
                          className="legal-callout"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                          </svg>
                          <p>{renderInline(block.text)}</p>
                        </div>
                      );
                    }
                    return null;
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
